import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { getSocket } from '../api/socket';
import BidderAuctionView from '../components/BidderAuctionView';

function getAuctionRoundStatus(auction: any) {
  if (auction.status === 'CLOSED') return { text: 'Auction Closed', bg: '#fce8e6', color: '#c62828' };
  const active = auction.rounds?.find((r: any) => r.status === 'ACTIVE');
  if (active) return { text: `Round ${active.roundNumber} Open`, bg: '#e6f4ea', color: '#1e7e34' };
  const lastClosed = [...(auction.rounds ?? [])].reverse().find((r: any) => r.status === 'CLOSED');
  if (lastClosed) return { text: `Round ${lastClosed.roundNumber} Closed`, bg: '#fff3e0', color: '#e65100' };
  return { text: 'Auction Pending', bg: '#e8eaf6', color: '#3949ab' };
}

export default function BidderPage() {
  const { user, logout } = useAuth();
  const [auctions, setAuctions] = useState<any[]>([]);
  const [selectedAuction, setSelectedAuction] = useState<any | null>(null);

  useEffect(() => {
    loadAuctions();
  }, []);

  useEffect(() => {
    const refresh = () => loadAuctions();
    const s = getSocket();
    if (s) {
      s.on('auction:created', refresh);
      s.on('auction:deleted', refresh);
      return () => {
        s.off('auction:created', refresh);
        s.off('auction:deleted', refresh);
      };
    }
  }, []);

  async function loadAuctions() {
    const { data } = await api.get('/auctions');
    setAuctions(data);
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h2 style={{ margin: 0 }}>Auction Platform — Bidder</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>{user?.name}</span>
          <button style={styles.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </header>
      <div style={styles.content}>
        {selectedAuction ? (
          <BidderAuctionView auction={selectedAuction} onBack={() => setSelectedAuction(null)} />
        ) : (
          <>
            <h3>Available Auctions</h3>
            {auctions.length === 0 && <p style={{ color: '#888' }}>No auctions available yet.</p>}
            <div style={styles.grid}>
              {[...auctions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(auction => {
                const rs = getAuctionRoundStatus(auction);
                return (
                  <div key={auction.id} style={styles.card}>
                    <h4 style={{ margin: '0 0 0.5rem' }}>{auction.title}</h4>
                    {auction.description && <p style={styles.desc}>{auction.description}</p>}
                    <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', background: rs.bg, color: rs.color }}>{rs.text}</span>
                    <p style={styles.meta}>{auction.categories.length} categories &bull; {auction.rounds.length} rounds so far</p>
                    <button style={styles.btn} onClick={() => setSelectedAuction(auction)}>
                      {auction.status === 'ACTIVE' ? 'Join Auction' : 'View Details'}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles: any = {
  page: { minHeight: '100vh', background: '#f0f2f5' },
  header: { background: '#1a1a2e', color: 'white', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  content: { padding: '2rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' },
  card: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' },
  desc: { color: '#666', fontSize: '0.875rem', margin: '0 0 0.75rem' },
  meta: { color: '#888', fontSize: '0.8rem', margin: '0.5rem 0' },
  badge: (status: string) => ({
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold',
    background: status === 'ACTIVE' ? '#e6f4ea' : status === 'CLOSED' ? '#fce8e6' : '#e8eaf6',
    color: status === 'ACTIVE' ? '#1e7e34' : status === 'CLOSED' ? '#c62828' : '#3949ab',
  }),
  btn: { marginTop: '0.75rem', width: '100%', padding: '0.5rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  logoutBtn: { padding: '0.4rem 0.8rem', background: 'transparent', border: '1px solid white', color: 'white', borderRadius: '4px', cursor: 'pointer' },
};
